'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Home, RefreshCw } from 'lucide-react';
import { IconWrapper } from './icon-wrapper';
import LogoIcon from '/public/logo.svg';
import i18n from '@/i18n/config';
import { reportJSError } from '@/lib/monitor';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  resetKeys?: any[];
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

/**
 * 错误边界组件
 * 捕获子组件树中的 JavaScript 错误，记录错误并显示备用 UI
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('[ErrorBoundary] getDerivedStateFromError called:', {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
    });
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Client-side error caught:', error, errorInfo);

    // 调用自定义错误处理函数（如果提供）
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 上报错误到监控系统
    try {
      console.log('[ErrorBoundary] 正在上报错误到监控系统...');
      reportJSError(error, {
        type: 'react-error-boundary',
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        page: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      console.log('[ErrorBoundary] 错误上报成功');
    } catch (reportError) {
      console.error('[ErrorBoundary] 错误上报失败:', reportError);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // 当 resetKeys 改变时，重置错误状态
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      prevProps.resetKeys &&
      this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index],
      )
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary(): void {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      console.log('[ErrorBoundary] 渲染错误页面:', {
        errorName: this.state.error?.name,
        errorMessage: this.state.error?.message,
        hasCustomFallback: !!this.props.fallback,
      });

      // 使用自定义的 fallback UI（如果提供）
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认的错误 UI
      return (
        <div className="flex flex-col h-screen items-center justify-center p-6 text-center">
          {/* <div className="mb-6 flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div> */}

          <IconWrapper icon={LogoIcon} size={80} className="mb-6" />
          <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">
            {i18n.t('error:boundary.title')}
          </h1>

          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            {i18n.t('error:boundary.message')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={this.resetErrorBoundary}
              className="flex items-center gap-2"
              variant="default"
            >
              <RefreshCw className="w-4 h-4" />
              {i18n.t('error:boundary.retry')}
            </Button>

            <Button
              asChild
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              <span>
                <Home className="w-4 h-4" />
                {i18n.t('common:backToHome')}
              </span>
            </Button>
          </div>

          {process.env.NEXT_PUBLIC_ENV_MODE === 'LOCAL' && this.state.error && (
            <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-md text-left w-full max-w-2xl overflow-auto">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                错误详情（仅开发环境可见）：
              </p>
              <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                {this.state.error.stack || this.state.error.message}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
